/**
 * Which row history the workspace records, and how long it keeps it.
 *
 * The plan is the ceiling: an admin can record less and keep it for less time,
 * never more. Turning a kind of change or a channel off also means those
 * changes cannot be reverted, because reverting reads the history.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/errors'
import type { RowHistorySettings } from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Separator } from '@/shared/ui/separator'
import { Skeleton } from '@/shared/ui/skeleton'
import {
  HISTORY_ACTIONS,
  HISTORY_SOURCES,
  draftFrom,
  historyBody,
  retentionProblem,
  toggled,
  type HistoryDraft,
} from '../history'
import { useRowHistorySettings, useUpdateRowHistory } from '../history-queries'

export function HistorySection({ workspaceId }: { workspaceId: number }) {
  const { data: settings } = useRowHistorySettings(workspaceId)

  if (settings === undefined) return <Skeleton className="h-64" />

  return <HistoryForm workspaceId={workspaceId} settings={settings} />
}

function HistoryForm({
  workspaceId,
  settings,
}: {
  workspaceId: number
  settings: RowHistorySettings
}) {
  const { t, i18n } = useTranslation()
  const save = useUpdateRowHistory(workspaceId)

  const [draft, setDraft] = useState<HistoryDraft>(() => draftFrom(settings))
  const [saved, setSaved] = useState(false)
  const [refused, setRefused] = useState<string | null>(null)

  const planDays = settings.plan_retention_days
  const notRecorded = planDays === 0
  const problem = retentionProblem(draft.retention, planDays)

  function change(values: Partial<HistoryDraft>): void {
    setSaved(false)
    setDraft((current) => ({ ...current, ...values }))
  }

  return (
    <div className="space-y-4">
      <h3 className="text-base font-medium">{t('rowHistorySettings.title')}</h3>
      <p className="text-sm text-muted-foreground">
        {t('rowHistorySettings.description')}
      </p>

      {notRecorded && (
        <Alert>
          <AlertTitle>{t('rowHistorySettings.notRecordedTitle')}</AlertTitle>
          <AlertDescription>
            {t('rowHistorySettings.notRecordedDescription')}
          </AlertDescription>
        </Alert>
      )}

      <Separator />

      <section className="space-y-3">
        <h4 className="font-medium">{t('rowHistorySettings.retentionTitle')}</h4>
        <p className="text-sm text-muted-foreground">
          {planDays === null
            ? t('rowHistorySettings.planKeepsForever')
            : t('rowHistorySettings.planKeepsDays', { count: planDays })}{' '}
          {settings.plan_max_entries !== null &&
            t('rowHistorySettings.planMaxEntries', {
              count: settings.plan_max_entries,
              formatted: settings.plan_max_entries.toLocaleString(i18n.language),
            })}
        </p>

        <div className="space-y-1.5">
          <Label htmlFor="history-retention">{t('rowHistorySettings.retention')}</Label>
          <Input
            id="history-retention"
            inputMode="numeric"
            className="w-40"
            disabled={notRecorded}
            aria-invalid={problem !== null}
            value={draft.retention}
            placeholder={
              planDays === null
                ? t('rowHistorySettings.retentionPlaceholderForever')
                : String(planDays)
            }
            onChange={(event) => change({ retention: event.target.value })}
          />
          <p className="text-sm text-muted-foreground">
            {t('rowHistorySettings.retentionHelper')}
          </p>
          {problem === 'invalid' && (
            <p className="text-sm text-destructive">
              {t('rowHistorySettings.retentionInvalid')}
            </p>
          )}
          {problem === 'exceedsPlan' && (
            <p className="text-sm text-destructive">
              {t('rowHistorySettings.retentionExceedsPlan', { count: planDays ?? 0 })}
            </p>
          )}
        </div>
      </section>

      <Separator />

      <section className="space-y-3">
        <h4 className="font-medium">{t('rowHistorySettings.recordedTitle')}</h4>
        <p className="text-sm text-muted-foreground">
          {t('rowHistorySettings.recordedDescription')}
        </p>

        <div className="grid gap-6 sm:grid-cols-2">
          <fieldset className="space-y-2" disabled={notRecorded}>
            <legend className="mb-2 text-sm font-medium">
              {t('rowHistorySettings.actionsLegend')}
            </legend>
            {HISTORY_ACTIONS.map((action) => (
              <Toggle
                key={action}
                id={`history-action-${action}`}
                label={t(`rowHistorySettings.action.${action}`)}
                checked={!draft.disabledActions.includes(action)}
                onChange={(on) =>
                  change({ disabledActions: toggled(draft.disabledActions, action, on) })
                }
              />
            ))}
          </fieldset>

          <fieldset className="space-y-2" disabled={notRecorded}>
            <legend className="mb-2 text-sm font-medium">
              {t('rowHistorySettings.sourcesLegend')}
            </legend>
            {HISTORY_SOURCES.map((source) => (
              <Toggle
                key={source}
                id={`history-source-${source}`}
                label={t(`rowHistorySettings.source.${source}`)}
                checked={!draft.disabledSources.includes(source)}
                onChange={(on) =>
                  change({ disabledSources: toggled(draft.disabledSources, source, on) })
                }
              />
            ))}
          </fieldset>
        </div>

        {(draft.disabledActions.length > 0 || draft.disabledSources.length > 0) && (
          <p className="text-sm text-muted-foreground">
            {t('rowHistorySettings.revertWarning')}
          </p>
        )}
      </section>

      {refused !== null && <p className="text-sm text-destructive">{refused}</p>}

      <div className="flex items-center justify-end gap-3">
        {saved && !save.isPending && (
          <p className="text-sm text-muted-foreground">{t('rowHistorySettings.saved')}</p>
        )}
        <Button
          disabled={save.isPending || notRecorded || problem !== null}
          onClick={() => {
            setRefused(null)
            save.mutate(historyBody(draft), {
              onSuccess: (answer) => {
                setSaved(true)
                setDraft(draftFrom(answer))
              },
              onError: (error) => {
                setRefused(
                  error instanceof ApiError
                    ? error.userMessage().description
                    : t('rowHistorySettings.saveFailed'),
                )
              },
            })
          }}
        >
          {t('rowHistorySettings.save')}
        </Button>
      </div>
    </div>
  )
}

function Toggle({
  id,
  label,
  checked,
  onChange,
}: {
  id: string
  label: string
  checked: boolean
  onChange: (on: boolean) => void
}) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(state) => {
          onChange(state === true)
        }}
      />
      <Label htmlFor={id} className="font-normal">
        {label}
      </Label>
    </div>
  )
}
